import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderSchedulingProspects: build.query<
      GetV2ProviderSchedulingProspectsApiResponse,
      GetV2ProviderSchedulingProspectsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
    deleteV2ProviderSchedulingProspectsByBookingProspectId: build.mutation<
      DeleteV2ProviderSchedulingProspectsByBookingProspectIdApiResponse,
      DeleteV2ProviderSchedulingProspectsByBookingProspectIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects/${queryArg.bookingProspectId}`,
        method: "DELETE",
      }),
    }),
    getV2ProviderSchedulingProspectsByBookingProspectId: build.query<
      GetV2ProviderSchedulingProspectsByBookingProspectIdApiResponse,
      GetV2ProviderSchedulingProspectsByBookingProspectIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects/${queryArg.bookingProspectId}`,
      }),
    }),
    postV2ProviderSchedulingProspectsByBookingProspectIdAccept: build.mutation<
      PostV2ProviderSchedulingProspectsByBookingProspectIdAcceptApiResponse,
      PostV2ProviderSchedulingProspectsByBookingProspectIdAcceptApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects/${queryArg.bookingProspectId}/accept`,
        method: "POST",
        body: queryArg.acceptProspectRequest,
      }),
    }),
    getV2ProviderSchedulingProspectsByBookingProspectIdMatches: build.query<
      GetV2ProviderSchedulingProspectsByBookingProspectIdMatchesApiResponse,
      GetV2ProviderSchedulingProspectsByBookingProspectIdMatchesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects/${queryArg.bookingProspectId}/matches`,
      }),
    }),
    postV2ProviderSchedulingProspectsByBookingProspectIdPromote: build.mutation<
      PostV2ProviderSchedulingProspectsByBookingProspectIdPromoteApiResponse,
      PostV2ProviderSchedulingProspectsByBookingProspectIdPromoteApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/scheduling/prospects/${queryArg.bookingProspectId}/promote`,
        method: "POST",
        body: queryArg.promoteProspectRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderSchedulingProspectsApiResponse =
  /** status 200 OK */ BookingProspectListResponse;
export type GetV2ProviderSchedulingProspectsApiArg = {
  /** Page size */
  limit?: number;
  /** Page offset */
  offset?: number;
};
export type DeleteV2ProviderSchedulingProspectsByBookingProspectIdApiResponse =
  /** status 200 OK */ BookingProspectResponse;
export type DeleteV2ProviderSchedulingProspectsByBookingProspectIdApiArg = {
  bookingProspectId: string;
};
export type GetV2ProviderSchedulingProspectsByBookingProspectIdApiResponse =
  /** status 200 OK */ BookingProspectResponse;
export type GetV2ProviderSchedulingProspectsByBookingProspectIdApiArg = {
  bookingProspectId: string;
};
export type PostV2ProviderSchedulingProspectsByBookingProspectIdAcceptApiResponse =
  /** status 200 OK */ AcceptProspectResponse;
export type PostV2ProviderSchedulingProspectsByBookingProspectIdAcceptApiArg = {
  bookingProspectId: string;
  acceptProspectRequest: AcceptProspectRequest;
};
export type GetV2ProviderSchedulingProspectsByBookingProspectIdMatchesApiResponse =
  /** status 200 OK */ ProspectMatchesResponse;
export type GetV2ProviderSchedulingProspectsByBookingProspectIdMatchesApiArg = {
  bookingProspectId: string;
};
export type PostV2ProviderSchedulingProspectsByBookingProspectIdPromoteApiResponse =
  /** status 200 OK */ PromoteProspectResponse;
export type PostV2ProviderSchedulingProspectsByBookingProspectIdPromoteApiArg =
  {
    bookingProspectId: string;
    promoteProspectRequest: PromoteProspectRequest;
  };
export type GateRequirementSchema = {
  /** before_calendar|at_booking|after_booking */
  checkpoint: string;
  /** Gate kind: paperwork|card|eligibility|fit */
  gate: string;
  /** pass|fail|pending */
  outcome: string;
  /** Human-readable explanation for fail/pending */
  reason?: string | null;
  /** Practice-facing gate title */
  title?: string | null;
};
export type BookingProspectSchema = {
  /** The pending (suggested) appointment this booking created */
  appointment_id?: string | null;
  /** Start time of the linked appointment */
  appointment_start_time?: string | null;
  /** Status of the linked appointment (suggested until confirmed) */
  appointment_status?: string | null;
  booking_prospect_id: string;
  created_at?: string | null;
  dob?: string | null;
  /** Duration of the linked appointment in minutes */
  duration_minutes?: number | null;
  email?: string | null;
  /** Encounter type of the linked appointment */
  encounter_type_id?: string | null;
  first_name?: string | null;
  /** Scheduling gate results for the booking (why it is held): paperwork/card/eligibility outcomes */
  gate_results?: GateRequirementSchema[];
  /** True once the booker's card capture succeeded */
  has_card_on_file?: boolean;
  last_name?: string | null;
  phone?: string | null;
  promoted_patient_id?: string | null;
  /** Calendar owner the booking targets */
  provider_id: string;
  /** Visit reason the booker entered */
  reason?: string | null;
  /** True when the booking re-entered review because the patient moved it via the self-serve manage link (GDN-2272) */
  rescheduled_by_patient?: boolean;
  status: string;
};
export type BookingProspectListResponse = {
  items: BookingProspectSchema[];
  /** Count of items in this page */
  total: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type GateRequirementSchema2 = {
  /** before_calendar|at_booking|after_booking */
  checkpoint: string;
  /** Gate kind: paperwork|card|eligibility|fit */
  gate: string;
  /** pass|fail|pending */
  outcome: string;
  /** Human-readable explanation for fail/pending */
  reason?: string | null;
  /** Practice-facing gate title */
  title?: string | null;
};
export type BookingProspectSchema2 = {
  /** The pending (suggested) appointment this booking created */
  appointment_id?: string | null;
  /** Start time of the linked appointment */
  appointment_start_time?: string | null;
  /** Status of the linked appointment (suggested until confirmed) */
  appointment_status?: string | null;
  booking_prospect_id: string;
  created_at?: string | null;
  dob?: string | null;
  /** Duration of the linked appointment in minutes */
  duration_minutes?: number | null;
  email?: string | null;
  /** Encounter type of the linked appointment */
  encounter_type_id?: string | null;
  first_name?: string | null;
  /** Scheduling gate results for the booking (why it is held): paperwork/card/eligibility outcomes */
  gate_results?: GateRequirementSchema2[];
  /** True once the booker's card capture succeeded */
  has_card_on_file?: boolean;
  last_name?: string | null;
  phone?: string | null;
  promoted_patient_id?: string | null;
  /** Calendar owner the booking targets */
  provider_id: string;
  /** Visit reason the booker entered */
  reason?: string | null;
  /** True when the booking re-entered review because the patient moved it via the self-serve manage link (GDN-2272) */
  rescheduled_by_patient?: boolean;
  status: string;
};
export type BookingProspectResponse = {
  prospect: BookingProspectSchema2;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ProspectMatchCandidateSchema = {
  /** Match confidence, 0-100 */
  confidence: number;
  dob?: string | null;
  /** Candidate's email on file, for comparison */
  email?: string | null;
  name?: string | null;
  patient_id: string;
  /** Candidate's mobile phone on file, for comparison */
  phone?: string | null;
};
export type GateRequirementSchema3 = {
  /** before_calendar|at_booking|after_booking */
  checkpoint: string;
  /** Gate kind: paperwork|card|eligibility|fit */
  gate: string;
  /** pass|fail|pending */
  outcome: string;
  /** Human-readable explanation for fail/pending */
  reason?: string | null;
  /** Practice-facing gate title */
  title?: string | null;
};
export type BookingProspectSchema3 = {
  /** The pending (suggested) appointment this booking created */
  appointment_id?: string | null;
  /** Start time of the linked appointment */
  appointment_start_time?: string | null;
  /** Status of the linked appointment (suggested until confirmed) */
  appointment_status?: string | null;
  booking_prospect_id: string;
  created_at?: string | null;
  dob?: string | null;
  /** Duration of the linked appointment in minutes */
  duration_minutes?: number | null;
  email?: string | null;
  /** Encounter type of the linked appointment */
  encounter_type_id?: string | null;
  first_name?: string | null;
  /** Scheduling gate results for the booking (why it is held): paperwork/card/eligibility outcomes */
  gate_results?: GateRequirementSchema3[];
  /** True once the booker's card capture succeeded */
  has_card_on_file?: boolean;
  last_name?: string | null;
  phone?: string | null;
  promoted_patient_id?: string | null;
  /** Calendar owner the booking targets */
  provider_id: string;
  /** Visit reason the booker entered */
  reason?: string | null;
  /** True when the booking re-entered review because the patient moved it via the self-serve manage link (GDN-2272) */
  rescheduled_by_patient?: boolean;
  status: string;
};
export type AcceptProspectResponse = {
  /** True when the booking was accepted */
  accepted: boolean;
  /** Ranked match candidates when decision_required */
  candidates?: ProspectMatchCandidateSchema[];
  /** Whether acceptance created a new patient record */
  created_new_patient?: boolean;
  /** True when possible existing-patient matches need a staff link-or-create choice; retry with patient_id or create_new */
  decision_required?: boolean;
  /** The resolved patient once accepted */
  patient_id?: string | null;
  /** The prospect after acceptance */
  prospect?: BookingProspectSchema3 | null;
};
export type AcceptProspectRequest = {
  /** Create a new patient from the booking details */
  create_new?: boolean;
  /** Link the booking to this existing patient */
  patient_id?: string | null;
};
export type ProspectMatchCandidateSchema2 = {
  /** Match confidence, 0-100 */
  confidence: number;
  dob?: string | null;
  /** Candidate's email on file, for comparison */
  email?: string | null;
  name?: string | null;
  patient_id: string;
  /** Candidate's mobile phone on file, for comparison */
  phone?: string | null;
};
export type ProspectMatchesResponse = {
  items: ProspectMatchCandidateSchema2[];
  total: number;
};
export type GateRequirementSchema4 = {
  /** before_calendar|at_booking|after_booking */
  checkpoint: string;
  /** Gate kind: paperwork|card|eligibility|fit */
  gate: string;
  /** pass|fail|pending */
  outcome: string;
  /** Human-readable explanation for fail/pending */
  reason?: string | null;
  /** Practice-facing gate title */
  title?: string | null;
};
export type BookingProspectSchema4 = {
  /** The pending (suggested) appointment this booking created */
  appointment_id?: string | null;
  /** Start time of the linked appointment */
  appointment_start_time?: string | null;
  /** Status of the linked appointment (suggested until confirmed) */
  appointment_status?: string | null;
  booking_prospect_id: string;
  created_at?: string | null;
  dob?: string | null;
  /** Duration of the linked appointment in minutes */
  duration_minutes?: number | null;
  email?: string | null;
  /** Encounter type of the linked appointment */
  encounter_type_id?: string | null;
  first_name?: string | null;
  /** Scheduling gate results for the booking (why it is held): paperwork/card/eligibility outcomes */
  gate_results?: GateRequirementSchema4[];
  /** True once the booker's card capture succeeded */
  has_card_on_file?: boolean;
  last_name?: string | null;
  phone?: string | null;
  promoted_patient_id?: string | null;
  /** Calendar owner the booking targets */
  provider_id: string;
  /** Visit reason the booker entered */
  reason?: string | null;
  /** True when the booking re-entered review because the patient moved it via the self-serve manage link (GDN-2272) */
  rescheduled_by_patient?: boolean;
  status: string;
};
export type PromoteProspectResponse = {
  created_new_patient: boolean;
  /** Patient the booking now belongs to */
  patient_id: string;
  prospect: BookingProspectSchema4;
};
export type PromoteProspectRequest = {
  /** Create a new patient from the prospect's contact info */
  create_new?: boolean;
  /** Existing patient to link the booking to */
  patient_id?: string | null;
};
